/**
 * Obstruction Filters Slice
 *
 * Filter state + API params builder for the obstructions list. Mirrors
 * `taskFiltersSlice` (richer multi-select shape) — fits the `utrudnienia`
 * project §4.3 filter contract (types, statuses, severities, impacts,
 * location, contractor, dateFrom/dateTo, sortBy, sortDir, page, pageSize).
 *
 * Every filter mutation resets `page` to `DEFAULT_PAGE` (paginated-list
 * Rule 1 — BLOCKING).
 */
import {
  ObstructionType,
  ObstructionStatus,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import type { ObstructionFiltersParams } from '@/types/api/requests';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/pagination';

// ===== SORT FIELD TYPE =====

export type ObstructionSortField =
  | 'createdAt'
  | 'updatedAt'
  | 'title'
  | 'plannedStartDate'
  | 'plannedEndDate'
  | 'status'
  | 'severity';

/** Sort direction discriminator — matches backend `sortDir` shape (`asc`/`desc`). */
export type ObstructionSortDir = 'asc' | 'desc';

// ===== SLICE INTERFACE =====

export interface ObstructionFiltersSlice {
  obstructionFilters: {
    search: string;
    types: ObstructionType[];
    statuses: ObstructionStatus[];
    severities: SeverityLevel[];
    impacts: TrafficImpact[];
    location: string;
    contractor: string;
    dateFrom: Date | null;
    dateTo: Date | null;
    sortBy: ObstructionSortField;
    sortDir: ObstructionSortDir;
    page: number;
    pageSize: number;

    // Actions
    setSearch: (search: string) => void;
    toggleType: (type: ObstructionType) => void;
    setTypes: (types: ObstructionType[]) => void;
    toggleStatus: (status: ObstructionStatus) => void;
    setStatuses: (statuses: ObstructionStatus[]) => void;
    toggleSeverity: (severity: SeverityLevel) => void;
    setSeverities: (severities: SeverityLevel[]) => void;
    toggleImpact: (impact: TrafficImpact) => void;
    setImpacts: (impacts: TrafficImpact[]) => void;
    setLocation: (location: string) => void;
    setContractor: (contractor: string) => void;
    setDateRange: (from: Date | null, to: Date | null) => void;
    setSortBy: (sortBy: ObstructionSortField) => void;
    setSortDir: (sortDir: ObstructionSortDir) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    resetFilters: () => void;
  };
}

// ===== SLICE CREATOR =====

/** Toggle a value into a draft array in place (add if missing, remove if present). */
function toggleInDraftArray<T>(list: T[], value: T): void {
  const index = list.indexOf(value);
  if (index === -1) {
    list.push(value);
  } else {
    list.splice(index, 1);
  }
}

export const createObstructionFiltersSlice = (
  set: (fn: (state: ObstructionFiltersSlice) => void) => void,
  _get: () => unknown,
  _store: unknown
) => ({
  obstructionFilters: {
    search: '',
    types: [] as ObstructionType[],
    statuses: [] as ObstructionStatus[],
    severities: [] as SeverityLevel[],
    impacts: [] as TrafficImpact[],
    location: '',
    contractor: '',
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    sortBy: 'createdAt' as ObstructionSortField,
    sortDir: 'desc' as ObstructionSortDir,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,

    setSearch: (search: string) => {
      set((state) => {
        state.obstructionFilters.search = search;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    toggleType: (type: ObstructionType) => {
      set((state) => {
        toggleInDraftArray(state.obstructionFilters.types, type);
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setTypes: (types: ObstructionType[]) => {
      set((state) => {
        state.obstructionFilters.types = types;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    toggleStatus: (status: ObstructionStatus) => {
      set((state) => {
        toggleInDraftArray(state.obstructionFilters.statuses, status);
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setStatuses: (statuses: ObstructionStatus[]) => {
      set((state) => {
        state.obstructionFilters.statuses = statuses;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    toggleSeverity: (severity: SeverityLevel) => {
      set((state) => {
        toggleInDraftArray(state.obstructionFilters.severities, severity);
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setSeverities: (severities: SeverityLevel[]) => {
      set((state) => {
        state.obstructionFilters.severities = severities;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    toggleImpact: (impact: TrafficImpact) => {
      set((state) => {
        toggleInDraftArray(state.obstructionFilters.impacts, impact);
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setImpacts: (impacts: TrafficImpact[]) => {
      set((state) => {
        state.obstructionFilters.impacts = impacts;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setLocation: (location: string) => {
      set((state) => {
        state.obstructionFilters.location = location;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setContractor: (contractor: string) => {
      set((state) => {
        state.obstructionFilters.contractor = contractor;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setDateRange: (from: Date | null, to: Date | null) => {
      set((state) => {
        state.obstructionFilters.dateFrom = from;
        state.obstructionFilters.dateTo = to;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setSortBy: (sortBy: ObstructionSortField) => {
      set((state) => {
        state.obstructionFilters.sortBy = sortBy;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setSortDir: (sortDir: ObstructionSortDir) => {
      set((state) => {
        state.obstructionFilters.sortDir = sortDir;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    setPage: (page: number) => {
      set((state) => {
        state.obstructionFilters.page = page;
      });
    },

    setPageSize: (pageSize: number) => {
      set((state) => {
        state.obstructionFilters.pageSize = pageSize;
        if (state.obstructionFilters.page !== DEFAULT_PAGE)
          state.obstructionFilters.page = DEFAULT_PAGE;
      });
    },

    resetFilters: () => {
      set((state) => {
        state.obstructionFilters.search = '';
        state.obstructionFilters.types = [];
        state.obstructionFilters.statuses = [];
        state.obstructionFilters.severities = [];
        state.obstructionFilters.impacts = [];
        state.obstructionFilters.location = '';
        state.obstructionFilters.contractor = '';
        state.obstructionFilters.dateFrom = null;
        state.obstructionFilters.dateTo = null;
        state.obstructionFilters.sortBy = 'createdAt';
        state.obstructionFilters.sortDir = 'desc';
        state.obstructionFilters.page = DEFAULT_PAGE;
        state.obstructionFilters.pageSize = DEFAULT_PAGE_SIZE;
      });
    },
  },
});

// ===== API PARAMS BUILDER =====

/**
 * Convert obstruction filter slice state to API request params.
 * Maps slice field names to the backend `GetObstructionsQuery` contract
 * (comma-separated multi-select filters, ISO date strings).
 */
export function buildObstructionFiltersParams(
  filters: Pick<
    ObstructionFiltersSlice['obstructionFilters'],
    | 'search'
    | 'types'
    | 'statuses'
    | 'severities'
    | 'impacts'
    | 'location'
    | 'contractor'
    | 'dateFrom'
    | 'dateTo'
    | 'sortBy'
    | 'sortDir'
    | 'page'
    | 'pageSize'
  >
): ObstructionFiltersParams {
  const params: ObstructionFiltersParams = {};

  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.types.length > 0) {
    params.types = filters.types.join(',');
  }
  if (filters.statuses.length > 0) {
    params.statuses = filters.statuses.join(',');
  }
  if (filters.severities.length > 0) {
    params.severities = filters.severities.join(',');
  }
  if (filters.impacts.length > 0) {
    params.impacts = filters.impacts.join(',');
  }
  if (filters.location) {
    params.location = filters.location;
  }
  if (filters.contractor) {
    params.contractor = filters.contractor;
  }
  if (filters.dateFrom) {
    params.dateFrom = filters.dateFrom.toISOString();
  }
  if (filters.dateTo) {
    params.dateTo = filters.dateTo.toISOString();
  }
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
  }
  if (filters.sortDir) {
    params.sortDir = filters.sortDir;
  }
  params.page = filters.page;
  params.pageSize = filters.pageSize;

  return params;
}
